/**
 * main.tsx — Entry point
 *
 * No token → LoginPage (SpecFlow-aligned design, unified Supabase project)
 * Has token → App (full workspace)
 *
 * Supabase project is configured via build-time env vars:
 *   VITE_SUPABASE_URL      — must match SpecFlow's Supabase project
 *   VITE_SUPABASE_ANON_KEY — must match SpecFlow's Supabase project
 */

import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import App, { ErrorBoundary } from "./App.js";
import { AcceptInvite } from "./AcceptInvite.js";
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from "./auth.js";

function clearStudioStorage() {
  Object.keys(localStorage)
    .filter(k => k.startsWith("studio_"))
    .forEach(k => localStorage.removeItem(k));
}

// Single owner of post-auth navigation. If the user arrived via an invite link
// while logged out, AcceptInvite stored studio_pending_invite_url before sending
// them to sign in — so return them straight to /accept (now with a live session)
// and the invite actually gets accepted. Otherwise reload into the workspace.
// Previously each login handler called window.location.reload() directly, which
// reloaded "/" and pre-empted the SIGNED_IN→pending redirect: accept never fired,
// so an invited user landed on their own workspace and never joined the folder.
function postAuthNav() {
  const pending = localStorage.getItem("studio_pending_invite_url");
  if (pending) { localStorage.removeItem("studio_pending_invite_url"); window.location.href = pending; return; }
  window.location.reload();
}

// Keep studio_token fresh; detect user switches; clean up on sign-out.
supabase.auth.onAuthStateChange((event, session) => {
  if (event === "SIGNED_IN" && session) {
    const oldUserId = localStorage.getItem("studio_user_id");
    const newUserId = session.user.id;
    // Capture the pending-invite redirect BEFORE clearStudioStorage() — it is a
    // navigation intent, not per-account data, and the user-switch wipe would
    // otherwise delete it. Re-set it after the wipe so postAuthNav() still works.
    const pendingInviteUrl = localStorage.getItem("studio_pending_invite_url");
    if (oldUserId && oldUserId !== newUserId) {
      // Different user logged in — wipe all cached data for the previous account
      clearStudioStorage();
      if (pendingInviteUrl) localStorage.setItem("studio_pending_invite_url", pendingInviteUrl);
    }
    localStorage.setItem("studio_token", session.access_token);
    if (session.refresh_token) localStorage.setItem("studio_refresh_token", session.refresh_token);
    localStorage.setItem("studio_user_id", newUserId);
    if (session.user.email) localStorage.setItem("studio_email", session.user.email);
    // Post-auth navigation is owned by postAuthNav() (called from the login
    // handlers); no redirect here, to avoid a competing href/reload race.
  }

  if (event === "SIGNED_OUT") {
    clearStudioStorage();
  }

  // Always sync token for auto-refresh events
  if (event === "TOKEN_REFRESHED" && session?.access_token) {
    localStorage.setItem("studio_token", session.access_token);
    if (session.refresh_token) localStorage.setItem("studio_refresh_token", session.refresh_token);
  }
});

// ---------------------------------------------------------------------------
// Shared style constants (mirrors SpecFlow auth CSS)
// ---------------------------------------------------------------------------

const MONO: React.CSSProperties = { fontFamily: "'Space Mono', monospace" };
const RAMA: React.CSSProperties = { fontFamily: "'Rama Gothic E', 'Rama Gothic M', system-ui" };

const AUTH_WRAP: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "center",
  minHeight: "100vh", background: "#000", ...MONO,
};

const AUTH_BOX: React.CSSProperties = {
  width: 420, maxWidth: "95vw",
  border: "1px solid #3a3a3a",
  background: "#111",
  padding: "32px 28px 28px",
};

const AUTH_LOGO: React.CSSProperties = {
  ...RAMA, fontSize: "1rem", fontWeight: 700,
  letterSpacing: "0.3em", textTransform: "uppercase" as const,
  color: "#f0f0f0", marginBottom: 24,
};

const AUTH_TABS: React.CSSProperties = {
  display: "flex", gap: 0, marginBottom: 24,
  borderBottom: "1px solid #242424",
};

const AUTH_FORM: React.CSSProperties = {
  display: "flex", flexDirection: "column" as const, gap: 12,
};

const AUTH_INPUT: React.CSSProperties = {
  ...MONO, background: "#000", border: "1px solid #3a3a3a",
  color: "#f0f0f0", fontSize: "0.9rem", letterSpacing: "0.03em",
  padding: "12px 16px", outline: "none",
  width: "100%", boxSizing: "border-box" as const, minHeight: 44,
};

const AUTH_SUBMIT: React.CSSProperties = {
  ...MONO, background: "#fff", color: "#000", border: "none",
  fontSize: "0.75rem", letterSpacing: "0.2em",
  textTransform: "uppercase" as const,
  width: "100%", height: 44, cursor: "pointer", marginTop: 4,
  boxSizing: "border-box" as const,
};

const GOOGLE_BTN: React.CSSProperties = {
  ...MONO, display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
  background: "#fff", color: "#111", border: "none",
  fontSize: "0.9rem", letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  width: "100%", height: 44, cursor: "pointer", boxSizing: "border-box" as const,
};

const CN_BTN: React.CSSProperties = {
  ...MONO, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
  background: "#0a0a0a", border: "1px solid #1e1e1e", color: "#555",
  fontSize: "0.6rem", letterSpacing: "0.1em",
  textTransform: "uppercase" as const,
  padding: 9, width: "100%", cursor: "not-allowed",
  boxSizing: "border-box" as const,
};

const FORGOT_LINK: React.CSSProperties = {
  ...MONO, background: "none", border: "none", color: "#555",
  fontSize: "0.58rem", letterSpacing: "0.08em",
  cursor: "pointer", padding: 0, textAlign: "right" as const, width: "100%",
};

const BACK_LINK: React.CSSProperties = {
  ...MONO, background: "none", border: "none", color: "#555",
  fontSize: "0.58rem", letterSpacing: "0.08em",
  cursor: "pointer", padding: 0, textAlign: "center" as const, width: "100%", marginTop: 4,
};

const DIVIDER: React.CSSProperties = {
  ...MONO, display: "flex", alignItems: "center", gap: 8,
  fontSize: "0.55rem", color: "#3a3a3a", letterSpacing: "0.1em",
  textAlign: "center" as const, margin: "4px 0",
};

const DIV_LINE: React.CSSProperties = { flex: 1, height: 1, background: "#242424" };

// ---------------------------------------------------------------------------
// Eye toggle icon
// ---------------------------------------------------------------------------

function EyeIcon({ show }: { show: boolean }) {
  return show ? (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// Password field with eye toggle
function PwField({ id, value, onChange, placeholder, show, onToggle }: {
  id: string; value: string; onChange: (v: string) => void;
  placeholder: string; show: boolean; onToggle: () => void;
}) {
  return (
    <div style={{ position: "relative" }}>
      <input
        id={id} type={show ? "text" : "password"} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ ...AUTH_INPUT, paddingRight: 38 }} autoComplete={id.includes("new") || id.includes("confirm") ? "new-password" : "current-password"}
      />
      <button type="button" onClick={onToggle} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#555", display: "flex", alignItems: "center", padding: 0 }}>
        <EyeIcon show={show} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LoginPage
// ---------------------------------------------------------------------------

type AuthTab  = "login" | "register";
type AuthView = "main" | "forgot" | "confirm-email" | "reset-pw";

function LoginPage() {
  const [tab,  setTab]  = useState<AuthTab>("login");
  const [view, setView] = useState<AuthView>("main");

  // Login
  const [loginEmail,    setLoginEmail]    = useState("");
  const [loginPwd,      setLoginPwd]      = useState("");
  const [showLoginPwd,  setShowLoginPwd]  = useState(false);
  const [loginError,    setLoginError]    = useState("");
  const [loginLoading,  setLoginLoading]  = useState(false);

  // Register
  const [regEmail,      setRegEmail]      = useState("");
  const [regPwd,        setRegPwd]        = useState("");
  const [regConfirm,    setRegConfirm]    = useState("");
  const [showRegPwd,    setShowRegPwd]    = useState(false);
  const [showRegConf,   setShowRegConf]   = useState(false);
  const [regError,      setRegError]      = useState("");
  const [regLoading,    setRegLoading]    = useState(false);
  const [confirmEmail,  setConfirmEmail]  = useState("");

  // Forgot password
  const [resetEmail,    setResetEmail]    = useState("");
  const [resetError,    setResetError]    = useState("");
  const [resetSuccess,  setResetSuccess]  = useState("");
  const [resetLoading,  setResetLoading]  = useState(false);

  // Password reset (recovery token from email link)
  const [recoveryToken, setRecoveryToken] = useState("");
  const [newPwd,        setNewPwd]        = useState("");
  const [newPwdConf,    setNewPwdConf]    = useState("");
  const [showNewPwd,    setShowNewPwd]    = useState(false);
  const [showNewPwdC,   setShowNewPwdC]   = useState(false);
  const [newPwdError,   setNewPwdError]   = useState("");
  const [newPwdLoading, setNewPwdLoading] = useState(false);

  // Check URL for OAuth or recovery token on mount
  useEffect(() => {
    // Legacy hash flow: #access_token= or #type=recovery
    const hash = window.location.hash.slice(1);
    if (hash) {
      const params = new URLSearchParams(hash);
      const token = params.get("access_token");
      const type  = params.get("type");
      if (token) {
        history.replaceState(null, "", window.location.pathname);
        if (type === "recovery") {
          setRecoveryToken(token);
          setView("reset-pw");
          return;
        }
        const userEmail = params.get("user_email") ?? params.get("email") ?? "";
        const refreshToken = params.get("refresh_token") ?? "";
        localStorage.setItem("studio_token", token);
        if (refreshToken) localStorage.setItem("studio_refresh_token", refreshToken);
        if (userEmail) localStorage.setItem("studio_email", userEmail);
        void supabase.auth.setSession({ access_token: token, refresh_token: refreshToken });
        postAuthNav();
        return;
      }
    }

    // PKCE flow: ?code= (exchange with Supabase directly)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    if (code) {
      history.replaceState(null, "", window.location.pathname);
      void (async () => {
        try {
          const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=pkce`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded", apikey: SUPABASE_ANON_KEY },
            body: new URLSearchParams({
              auth_code: code,
              code_verifier: sessionStorage.getItem("supabase_pkce_verifier") ?? "",
            }),
          });
          const data = await res.json() as { access_token?: string; refresh_token?: string; user?: { email?: string } };
          if (data.access_token) {
            localStorage.setItem("studio_token", data.access_token);
            if (data.refresh_token) localStorage.setItem("studio_refresh_token", data.refresh_token);
            localStorage.setItem("studio_email", data.user?.email ?? "");
            sessionStorage.removeItem("supabase_pkce_verifier");
            await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token ?? "" });
            postAuthNav();
          }
        } catch { /* silent — user can retry login */ }
      })();
    }
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────────────────

  async function handleLogin() {
    if (!loginEmail.trim() || !loginPwd) { setLoginError("Enter your email and password"); return; }
    setLoginLoading(true); setLoginError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPwd }),
      });
      const data = await res.json() as { access_token?: string; refresh_token?: string; error_description?: string; msg?: string; user?: { email?: string } };
      if (!res.ok) { setLoginError(data.error_description ?? data.msg ?? "Authentication failed"); return; }
      if (!data.access_token) { setLoginError("Check your email to confirm your account"); return; }
      localStorage.setItem("studio_token", data.access_token);
      if (data.refresh_token) localStorage.setItem("studio_refresh_token", data.refresh_token);
      localStorage.setItem("studio_email", data.user?.email ?? loginEmail.trim());
      await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token ?? "" });
      postAuthNav();
    } catch { setLoginError("Network error — please try again"); }
    finally { setLoginLoading(false); }
  }

  async function handleRegister() {
    if (!regEmail.trim() || !regPwd) return;
    if (regPwd !== regConfirm) { setRegError("Passwords do not match"); return; }
    if (regPwd.length < 8)     { setRegError("Password must be at least 8 characters"); return; }
    setRegLoading(true); setRegError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ email: regEmail.trim(), password: regPwd }),
      });
      const data = await res.json() as { access_token?: string; refresh_token?: string; error_description?: string; msg?: string };
      if (!res.ok) { setRegError(data.error_description ?? data.msg ?? "Registration failed"); return; }
      if (data.access_token) {
        localStorage.setItem("studio_token", data.access_token);
        if (data.refresh_token) localStorage.setItem("studio_refresh_token", data.refresh_token);
        localStorage.setItem("studio_email", regEmail.trim());
        await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token ?? "" });
        postAuthNav();
        return;
      }
      setConfirmEmail(regEmail.trim());
      setView("confirm-email");
    } catch { setRegError("Network error — please try again"); }
    finally { setRegLoading(false); }
  }

  async function handleResetPassword() {
    if (!resetEmail.trim()) return;
    setResetLoading(true); setResetError(""); setResetSuccess("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/recover`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ email: resetEmail.trim(), redirect_to: `${window.location.origin}` }),
      });
      if (!res.ok) {
        const d = await res.json() as { error_description?: string; msg?: string };
        setResetError(d.error_description ?? d.msg ?? "Failed to send reset link");
      } else {
        setResetSuccess("✉ Reset link sent — check your email");
      }
    } catch { setResetError("Network error — please try again"); }
    finally { setResetLoading(false); }
  }

  async function handleNewPassword() {
    if (!newPwd || !newPwdConf) return;
    if (newPwd !== newPwdConf) { setNewPwdError("Passwords do not match"); return; }
    if (newPwd.length < 8)     { setNewPwdError("Password must be at least 8 characters"); return; }
    setNewPwdLoading(true); setNewPwdError("");
    try {
      const res = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${recoveryToken}` },
        body: JSON.stringify({ password: newPwd }),
      });
      if (!res.ok) {
        const d = await res.json() as { error_description?: string; msg?: string };
        setNewPwdError(d.error_description ?? d.msg ?? "Failed to update password");
      } else {
        setView("main"); setTab("login");
        setLoginError(""); setLoginEmail("");
        setNewPwd(""); setNewPwdConf(""); setRecoveryToken("");
      }
    } catch { setNewPwdError("Network error — please try again"); }
    finally { setNewPwdLoading(false); }
  }

  function signInWithGoogle() {
    // SECURITY GATE (founder, 2026-06-13): if the user arrived via an invite link
    // (AcceptInvite stored the pending /accept URL before sending them to sign in),
    // send Google's OAuth redirect STRAIGHT BACK to /accept — not to "/". Returning
    // to "/" lets the auto-detected session render the workspace before the gate
    // runs (the race that let an un-invited Google account in). Returning to
    // /accept makes the email-binding gate deterministic for OAuth, exactly like
    // the email/password path (postAuthNav → /accept). Normal logins (no pending)
    // keep returning to the origin.
    const pending = localStorage.getItem("studio_pending_invite_url");
    const redirectTo = pending ?? window.location.origin;
    window.location.href = `${SUPABASE_URL}/auth/v1/authorize?provider=google&prompt=select_account&redirect_to=${encodeURIComponent(redirectTo)}`;
  }

  // ── Sub-views ─────────────────────────────────────────────────────────────────────────

  function switchTab(t: AuthTab) {
    setTab(t); setView("main");
    setLoginError(""); setRegError("");
  }

  function AuthDivider({ label = "── or ──" }: { label?: string }) {
    return (
      <div style={DIVIDER}>
        <span style={DIV_LINE} />
        <span>{label}</span>
        <span style={DIV_LINE} />
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────────────

  return (
    <div style={AUTH_WRAP}>
      <div style={AUTH_BOX}>

        {/* Logo */}
        <div style={AUTH_LOGO}>
          LANFINITAS <span style={{ color: "#888" }}>STUDIO</span>
        </div>

        {/* Reset password view */}
        {view === "reset-pw" && (
          <div style={AUTH_FORM}>
            <div style={{ ...MONO, fontSize: "0.65rem", color: "#aaa", lineHeight: 1.8, marginBottom: 4, textAlign: "center" }}>
              SET NEW PASSWORD
            </div>
            <PwField id="new-pw" value={newPwd} onChange={setNewPwd} placeholder="NEW PASSWORD (min 8 chars)" show={showNewPwd} onToggle={() => setShowNewPwd(v => !v)} />
            <PwField id="new-pw-confirm" value={newPwdConf} onChange={setNewPwdConf} placeholder="CONFIRM NEW PASSWORD" show={showNewPwdC} onToggle={() => setShowNewPwdC(v => !v)} />
            {newPwdError && <div style={{ ...MONO, fontSize: "0.65rem", color: "#f55", letterSpacing: "0.05em" }}>{newPwdError}</div>}
            <button onClick={() => { void handleNewPassword(); }} disabled={newPwdLoading || !newPwd || !newPwdConf} style={{ ...AUTH_SUBMIT, marginTop: 12, opacity: newPwdLoading ? 0.4 : 1, cursor: newPwdLoading ? "default" : "pointer" }}>
              {newPwdLoading ? "UPDATING…" : "UPDATE PASSWORD"}
            </button>
            <button style={BACK_LINK} onClick={() => { setView("main"); setTab("login"); }}>
              ← BACK TO LOGIN
            </button>
          </div>
        )}

        {/* Email confirmation view */}
        {view === "confirm-email" && (
          <div style={{ ...AUTH_FORM, textAlign: "center" }}>
            <div style={{ ...MONO, fontSize: "0.65rem", color: "#aaa", lineHeight: 1.8, marginBottom: 8 }}>
              ✉ Check your email to confirm your account.<br />
              <span style={{ color: "#555", fontSize: "0.6rem" }}>Click the link in the email to activate, then log in.</span>
            </div>
            <input type="email" defaultValue={confirmEmail} readOnly style={{ ...AUTH_INPUT, color: "#555" }} />
            <button style={BACK_LINK} onClick={() => { setView("main"); setTab("login"); }}>
              ← BACK TO LOGIN
            </button>
          </div>
        )}

        {/* Main login/register view */}
        {(view === "main" || view === "forgot") && (
          <>
            {/* Tabs */}
            <div style={AUTH_TABS}>
              {(["login", "register"] as AuthTab[]).map(t => (
                <button key={t} onClick={() => switchTab(t)} style={{
                  flex: 1, background: "transparent", border: "none",
                  borderBottom: tab === t ? "2px solid #fff" : "2px solid transparent",
                  color: tab === t ? "#f0f0f0" : "#555",
                  ...MONO, fontSize: "0.65rem", letterSpacing: "0.15em",
                  textTransform: "uppercase" as const, padding: "8px 0 10px",
                  cursor: "pointer", marginBottom: -1,
                }}>
                  {t === "login" ? "LOGIN" : "REGISTER"}
                </button>
              ))}
            </div>

            {/* LOGIN tab */}
            {tab === "login" && (
              <div style={AUTH_FORM}>

                {/* Login form inner (hidden when forgot-pw shown) */}
                {view === "main" && (
                  <>
                    <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                      placeholder="EMAIL" autoComplete="email"
                      onKeyDown={e => { if (e.key === "Enter") void handleLogin(); }}
                      style={AUTH_INPUT} />
                    <PwField id="login-pwd" value={loginPwd} onChange={setLoginPwd} placeholder="PASSWORD" show={showLoginPwd} onToggle={() => setShowLoginPwd(v => !v)} />
                    {loginError && <div style={{ ...MONO, fontSize: "0.65rem", color: "#f55", letterSpacing: "0.05em", marginTop: -4 }}>{loginError}</div>}
                    <button onClick={() => { void handleLogin(); }} disabled={loginLoading}
                      style={{ ...AUTH_SUBMIT, opacity: loginLoading ? 0.5 : 1, cursor: loginLoading ? "default" : "pointer" }}>
                      {loginLoading ? "…" : "LOGIN"}
                    </button>
                    <button style={FORGOT_LINK} onClick={() => { setView("forgot"); setResetEmail(loginEmail); setResetError(""); setResetSuccess(""); }}>
                      FORGOT PASSWORD?
                    </button>

                    <AuthDivider />

                    {/* Google */}
                    <button style={GOOGLE_BTN} onClick={signInWithGoogle}>
                      <span style={{ fontFamily: "Arial, sans-serif", fontSize: "0.85rem", fontWeight: 700, color: "#4285F4", letterSpacing: 0 }}>G</span>
                      <span>CONTINUE WITH GOOGLE</span>
                    </button>

                    <AuthDivider />

                    {/* CN placeholders */}
                    <button style={CN_BTN} disabled title="Coming soon">
                      <span style={{ fontSize: "0.75rem" }}>💬</span>
                      <span>WECHAT LOGIN (COMING SOON)</span>
                    </button>
                    <button style={CN_BTN} disabled title="Coming soon">
                      <span style={{ fontSize: "0.75rem" }}>📱</span>
                      <span>PHONE NUMBER (COMING SOON)</span>
                    </button>
                    <div style={{ ...MONO, fontSize: "0.58rem", color: "#555", textAlign: "center", marginTop: 6, letterSpacing: "0.05em" }}>
                      中国大陆用户登录方式即将开放
                    </div>
                  </>
                )}

                {/* Forgot password sub-form */}
                {view === "forgot" && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ ...MONO, fontSize: "0.6rem", color: "#888", lineHeight: 1.6, marginBottom: 4 }}>
                      Enter your email to receive a reset link.
                    </div>
                    <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                      placeholder="EMAIL" autoComplete="email" style={AUTH_INPUT} />
                    {resetError && <div style={{ ...MONO, fontSize: "0.65rem", color: "#f55", letterSpacing: "0.05em" }}>{resetError}</div>}
                    {resetSuccess && <div style={{ ...MONO, fontSize: "0.65rem", color: "#0a0", letterSpacing: "0.05em" }}>{resetSuccess}</div>}
                    <button onClick={() => { void handleResetPassword(); }} disabled={resetLoading || !resetEmail.trim()}
                      style={{ ...AUTH_SUBMIT, opacity: (resetLoading || !resetEmail.trim()) ? 0.4 : 1, cursor: (resetLoading || !resetEmail.trim()) ? "default" : "pointer" }}>
                      {resetLoading ? "SENDING…" : "SEND RESET LINK"}
                    </button>
                    <button style={BACK_LINK} onClick={() => setView("main")}>
                      ← BACK TO LOGIN
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* REGISTER tab */}
            {tab === "register" && (
              <div style={AUTH_FORM}>
                <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)}
                  placeholder="EMAIL" autoComplete="email" style={AUTH_INPUT} />
                <PwField id="reg-pwd" value={regPwd} onChange={setRegPwd} placeholder="PASSWORD (min 8 chars)" show={showRegPwd} onToggle={() => setShowRegPwd(v => !v)} />
                <PwField id="reg-confirm" value={regConfirm} onChange={setRegConfirm} placeholder="CONFIRM PASSWORD" show={showRegConf} onToggle={() => setShowRegConf(v => !v)} />
                {regError && <div style={{ ...MONO, fontSize: "0.65rem", color: "#f55", letterSpacing: "0.05em" }}>{regError}</div>}
                <button onClick={() => { void handleRegister(); }} disabled={regLoading || !regEmail || !regPwd || !regConfirm}
                  style={{ ...AUTH_SUBMIT, opacity: (regLoading || !regEmail || !regPwd || !regConfirm) ? 0.4 : 1, cursor: (regLoading || !regEmail || !regPwd || !regConfirm) ? "default" : "pointer" }}>
                  {regLoading ? "…" : "CREATE ACCOUNT"}
                </button>
                <div style={{ ...MONO, fontSize: "0.65rem", color: "#555", marginTop: 8, lineHeight: 1.6, textAlign: "center" }}>
                  By registering, you agree to our{" "}
                  <a href="/legal/tos-eu" target="_blank" rel="noopener noreferrer" style={{ color: "#888", textDecoration: "underline" }}>
                    Terms of Service
                  </a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");
const root = createRoot(container);

async function init() {
  const { data: { session } } = await supabase.auth.getSession();
  if (window.location.hash.includes("access_token")) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
  // Intercept /accept?token= before the normal app render
  if (window.location.pathname === "/accept") {
    const inviteToken = new URLSearchParams(window.location.search).get("token") ?? "";
    root.render(<React.StrictMode><ErrorBoundary><AcceptInvite inviteToken={inviteToken} /></ErrorBoundary></React.StrictMode>);
    return;
  }
  if (session?.access_token) {
    localStorage.setItem("studio_token", session.access_token);
    if (session.refresh_token) localStorage.setItem("studio_refresh_token", session.refresh_token);
    root.render(<React.StrictMode><ErrorBoundary><App /></ErrorBoundary></React.StrictMode>);
  } else if (localStorage.getItem("studio_token")) {
    // Fallback: token in storage but Supabase session not yet hydrated
    // (e.g. very first tick after a raw-URL login). Render App and let
    // onAuthStateChange refresh it in the background.
    root.render(<React.StrictMode><ErrorBoundary><App /></ErrorBoundary></React.StrictMode>);
  } else {
    root.render(<React.StrictMode><ErrorBoundary><LoginPage /></ErrorBoundary></React.StrictMode>);
  }
}
void init();
